import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CtaButtonComponent } from '../cta-button/cta-button.component';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth/auth.service';
import { MpCounterpartyAliasComponent } from '../mp-counterparty-alias/mp-counterparty-alias.component';
import { TransactionService } from '../../services/transaction/transaction.service';
import { PromptTransaction, Transaction } from '../../models/transaction.model';
import { ScreenService } from '../../services/screen/screen.service';
import { SegmentedControlComponent } from '../segmented-control/segmented-control.component';
import { GroupsService } from '../../services/groups/groups.service';
import { ToastService } from '../../services/toast/toast.service';
import { Group, GroupMember } from '../../models/group.model';
import { combineLatest } from 'rxjs';
import { CustomDropdownComponent } from '../custom-dropdown/custom-dropdown.component';
import { CategoryService } from '../../services/category/category.service';
import { CURRENCIES, DEFAULT_CURRENCY } from '../../constants/currency.constants';
import { TEXT_CONSTANTS } from '../../constants/text-constants';
import { SafeHtmlPipe } from '../../utils/safe-html.pipe';
import { AddonChipBarComponent } from '../addon-chip-bar/addon-chip-bar.component';
import { DescriptionAddonComponent } from '../description-addon/description-addon.component';
import { TagsAddonComponent } from '../tags-addon/tags-addon.component';
import { GroupShareAddonComponent } from '../group-share-addon/group-share-addon.component';
import { IncomeModalComponent } from '../income-modal/income-modal.component';

type AddOnKey = 'description' | 'share' | 'tags';

@Component({
  selector: 'transaction-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CtaButtonComponent,
    CustomDropdownComponent,
    SegmentedControlComponent,
    SafeHtmlPipe,
    AddonChipBarComponent,
    DescriptionAddonComponent,
    TagsAddonComponent,
    GroupShareAddonComponent,
    IncomeModalComponent,
    MpCounterpartyAliasComponent,
  ],
  templateUrl: './transaction-form.component.html',
  styleUrl: './transaction-form.component.css',
})
export class TransactionFormComponent implements OnChanges, OnInit {
  @Input() editingTransaction: Transaction | null = null;
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() promptSubmitted = new EventEmitter<any>();
  @Output() formClosed = new EventEmitter<void>();

  @ViewChild('promptInput') promptInput!: ElementRef<HTMLInputElement>;
  @ViewChild('amountInput') amountInput!: ElementRef<HTMLInputElement>;
  @ViewChild('categoryDropdown') categoryDropdown!: CustomDropdownComponent;

  private _authService = inject(AuthService);
  private _categoryService = inject(CategoryService);
  private _transactionService = inject(TransactionService);
  private _screenService = inject(ScreenService);
  private _groupsService = inject(GroupsService);
  private _toastService = inject(ToastService);
  private fb = inject(FormBuilder);

  activeGroup: Group | null = null;
  activeGroupMembers: GroupMember[] = [];
  availableGroups: { value: string, label: string }[] = [];
  allGroupsData: Group[] = [];
  defaultGroupId: string | null = null;
  selectedFormGroupMembers: GroupMember[] = [];

  splitPaidById: string | null = null;
  splitType: 'equal' | 'exact' | 'percentage' = 'equal';
  readonly SHARE_SPLIT_OPTIONS = [
    { key: 'equal', label: TEXT_CONSTANTS.TRANSACTIONS.SPLIT_EQUAL },
    { key: 'exact', label: TEXT_CONSTANTS.TRANSACTIONS.SPLIT_EXACT },
    { key: 'percentage', label: TEXT_CONSTANTS.TRANSACTIONS.SPLIT_PERCENTAGE },
  ];
  groupMembersList: GroupMember[] = [];
  splitsState: { [user_id: string]: { selected: boolean, value: number, name: string } } = {};
  memberSelectedForAddon: { [memberId: string]: boolean } = {};
  splitValuesForAddon: { [memberId: string]: number } = {};

  // TAR-325: toggle "Marcar como deuda" + anulación de split automático.
  generatesDebt = true;
  splitOverrideEnabled = false;
  showIncomeModal = false;

  form: FormGroup;
  promptForm: FormGroup;

  activeMode: 'prompt' | 'manual' | null = null;
  TEXT = TEXT_CONSTANTS;

  categories: { value: string, label: string }[] = [];
  currencies = CURRENCIES;

  types = [
    { value: 'expense', label: TEXT_CONSTANTS.TRANSACTIONS.TYPE_EXPENSE },
    { value: 'income', label: TEXT_CONSTANTS.TRANSACTIONS.TYPE_INCOME }
  ];

  tags: string[] = [];
  availableTags: string[] = [];
  showCalculator: boolean = false;

  // Add-on pattern (identical to fixed-expense-form)
  activeAddOns: Set<AddOnKey> = new Set();

  readonly ADD_ON_LIST: { key: AddOnKey; icon: string; label: string }[] = [
    { key: 'description', icon: 'notes', label: TEXT_CONSTANTS.TRANSACTIONS.DESCRIPTION },
    { key: 'share', icon: 'group', label: TEXT_CONSTANTS.TRANSACTIONS.ADD_ON_SHARE },
    { key: 'tags', icon: 'label', label: TEXT_CONSTANTS.TRANSACTIONS.TAGS },
  ];

  get inactiveAddOns() {
    return this.ADD_ON_LIST.filter(a => {
      if (a.key === 'share' && this.activeGroup) return false;
      // TAR-325 (Req 4.9): para ingresos, el chip "Compartir" solo se
      // ofrece si hay al menos un grupo "Todo en común" disponible.
      if (a.key === 'share' && this.isIncome) return this.comunGroupsOnly.length > 0 && !this.activeAddOns.has(a.key);
      return !this.activeAddOns.has(a.key);
    });
  }

  /** TAR-325 (Req 3.7, 4.9): un ingreso de grupo solo se puede cargar en un grupo "Todo en común". */
  get comunGroupsOnly(): Group[] {
    return this.allGroupsData.filter(g => g.division === 'common');
  }

  get selectedCategoryId(): string {
    const formValue = this.form.get('category_id')?.value;
    if (formValue) return formValue;
    const cat = this.editingTransaction?.category;
    if (!cat) return '';
    return typeof cat === 'object' ? (cat.id ?? '') : (cat as unknown as string);
  }

  activateAddOn(key: string): void {
    this.activeAddOns.add(key as AddOnKey);
    if (key === 'share' && !this.form.get('group_id')?.value && this.defaultGroupId) {
      this.onGroupChange(this.defaultGroupId);
    }
  }

  removeAddOn(key: AddOnKey): void {
    this.activeAddOns.delete(key);
    if (key === 'description') this.form.patchValue({ description: '' });
    if (key === 'tags') { this.tags = []; }
    if (key === 'share') {
      this.onGroupChange(null);
      this.splitValuesForAddon = {};
      this.memberSelectedForAddon = {};
      // Fully clear the shared-expense state so onSubmit() doesn't rebuild
      // group_id / paid_by / split_type / splits from stale values while editing.
      this.groupMembersList = [];
      this.selectedFormGroupMembers = [];
      this.splitsState = {};
      this.splitPaidById = null;
      this.splitType = 'equal';
      this.generatesDebt = true;
      this.splitOverrideEnabled = false;
    }
  }

  onGenerateDebtChanged(value: boolean): void {
    this.generatesDebt = value;
  }

  onSplitOverrideToggled(value: boolean): void {
    this.splitOverrideEnabled = value;
  }

  onOpenIncomeModal(): void {
    this.showIncomeModal = true;
  }

  onShareMemberToggled(memberId: string): void {
    this.memberSelectedForAddon[memberId] = !(this.memberSelectedForAddon[memberId] ?? true);
    if (this.splitsState[memberId]) {
      this.splitsState[memberId].selected = this.memberSelectedForAddon[memberId];
    }
  }

  onShareSplitValueChanged(event: { memberId: string; value: number }): void {
    this.splitValuesForAddon[event.memberId] = event.value;
    if (this.splitsState[event.memberId]) {
      this.splitsState[event.memberId].value = event.value;
    }
  }

  operatorClass = 'bg-neutral-soft dark:bg-dark-tertiary hover:bg-gray-100 dark:hover:bg-dark-border text-gray-700 dark:text-dark-text';

  calculatorOperators = [
    { value: '+', label: '+', class: this.operatorClass },
    { value: '-', label: '-', class: this.operatorClass },
    { value: '*', label: 'x', class: this.operatorClass },
    { value: '/', label: '/', class: this.operatorClass },
    { value: '=', label: '=', class: 'bg-gray-800 dark:bg-dark-border hover:bg-gray-700 text-white dark:text-dark-text' },
  ];

  constructor() {
    const today = new Date().toISOString().split('T')[0];

    this.form = this.fb.group({
      date: [today, Validators.required],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      description: [''],
      category_id: ['', Validators.required],
      type: ['expense', Validators.required],
      currency: [DEFAULT_CURRENCY, Validators.required],
      group_id: [''],
    });

    this.promptForm = this.fb.group({
      prompt: ['', Validators.required]
    });
  }

  private initGroupSplits(members: GroupMember[]) {
    this.groupMembersList = members;
    const currentUserId = this._authService.getCurrentUser()?.id;
    this.splitPaidById = currentUserId ?? null;
    this.splitType = 'equal';
    this.generatesDebt = true;
    this.splitOverrideEnabled = false;
    this.splitsState = {};
    this.memberSelectedForAddon = {};
    this.splitValuesForAddon = {};
    members.forEach(m => {
      this.splitsState[m.id] = { selected: true, value: 0, name: m.name || '' };
      this.memberSelectedForAddon[m.id] = true;
      this.splitValuesForAddon[m.id] = 0;
    });
  }

  ngOnInit(): void {
    this._categoryService.getCategoryOptions().subscribe(categories => {
      this.categories = categories;
    });

    this.availableTags = this._transactionService.getDistinctTags();

    if (!this.isEditing) {
      setTimeout(() => {
        this.promptInput?.nativeElement.focus();
      }, 150);
    }

    // groups$ emits the cached list immediately (BehaviorSubject), so no extra HTTP call.
    // It also re-emits when groups are created/deleted/updated.
    combineLatest([
      this._groupsService.getGroups(),
      this._groupsService.selectedGroupId$
    ]).subscribe(([groups, selectedId]) => {
      this.allGroupsData = groups;
      this.availableGroups = groups.map(g => ({ value: g.id, label: g.name }));
      this.defaultGroupId = groups.find(g => g.is_default)?.id ?? (groups.length === 1 ? groups[0].id : null);

      const group = selectedId ? groups.find(g => g.id === selectedId) ?? null : null;
      this.activeGroup = group;

      if (this.isEditing && this.editingTransaction?.group_id) {
        const editGroup = groups.find(g => g.id === this.editingTransaction!.group_id) ?? group;
        // La transacción que se edita puede pertenecer a un grupo distinto al
        // filtro global activo (p. ej. editando desde la página de un grupo
        // que no es el seleccionado en el sidebar) — usar siempre el grupo
        // propio de la transacción, no el del filtro global.
        this.activeGroup = editGroup ?? null;
        if (editGroup?.members) {
          this.activeGroupMembers = editGroup.members;
          this.applyEditingGroupState(this.editingTransaction!, editGroup.members, editGroup.division);
        }
        return;
      }

      if (group && group.members) {
        this.activeGroupMembers = group.members;
        this.initGroupSplits(group.members);
      } else {
        this.activeGroupMembers = [];
        if (this.selectedFormGroupMembers.length === 0) {
          this.groupMembersList = [];
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['editingTransaction'] && this.editingTransaction) {
      this.loadTransactionForEdit(this.editingTransaction);
    } else if (changes['editingTransaction'] && !this.editingTransaction) {
      this.resetForm();
    }
  }

  private loadTransactionForEdit(transaction: Transaction) {
    const formattedDate = new Date(transaction.date).toISOString().split('T')[0];

    this.form.patchValue({
      date: formattedDate,
      amount: Math.abs(transaction.amount),
      description: transaction.description,
      category_id: '',
      type: transaction.type,
      currency: (transaction as any).currency || DEFAULT_CURRENCY,
      group_id: transaction.group_id || ''
    });

    if (transaction.tags) {
      this.tags = [...transaction.tags];
    }

    if (transaction.category) {
      const categoryId = typeof transaction.category === 'string' ? transaction.category : transaction.category.id;
      this.form.patchValue({ category_id: categoryId });
    }

    // Auto-activate add-ons for fields that have values
    this.activeAddOns.clear();
    if (transaction.description) this.activeAddOns.add('description');
    if (transaction.tags?.length) this.activeAddOns.add('tags');
    if (transaction.group_id) this.activeAddOns.add('share');
  }

  private extractPaidById(paidBy: Transaction['paid_by']): string | null {
    if (!paidBy) return null;
    return typeof paidBy === 'string' ? paidBy : paidBy.id;
  }

  private applyEditingGroupState(transaction: Transaction, members: GroupMember[], division?: Group['division']): void {
    this.groupMembersList = members;
    this.selectedFormGroupMembers = members;
    this.splitPaidById = this.extractPaidById(transaction.paid_by) ?? this._authService.getCurrentUser()?.id ?? null;
    this.splitType = (transaction.split_type as 'equal' | 'exact' | 'percentage') || 'equal';
    this.generatesDebt = transaction.generates_debt ?? true;
    // El split manual solo cuenta como "anulado" si el grupo es de división
    // automática (según ingresos/porcentaje fijo) y la transacción ya trae
    // un split_type explícito — para "equal"/"fixed" clásico no aplica.
    this.splitOverrideEnabled = !!transaction.split_type && (division === 'income' || division === 'fixed');
    this.splitsState = {};
    this.memberSelectedForAddon = {};
    this.splitValuesForAddon = {};
    const splitsByUser = new Map((transaction.splits || []).map(s => [s.user_id, s]));
    members.forEach(m => {
      const split = splitsByUser.get(m.id);
      const selected = !!split;
      const value = this.splitType === 'percentage' ? (split?.percentage ?? 0) : (split?.amount ?? 0);
      this.splitsState[m.id] = { selected, value, name: m.name || '' };
      this.memberSelectedForAddon[m.id] = selected;
      this.splitValuesForAddon[m.id] = value;
    });
  }

  private resetForm() {
    const today = new Date().toISOString().split('T')[0];
    this.form.reset({
      date: today,
      amount: null,
      description: '',
      category_id: '',
      type: 'expense',
      currency: DEFAULT_CURRENCY,
      group_id: '',
    });
    this.tags = [];
    this.activeAddOns.clear();
    this.generatesDebt = true;
    this.splitOverrideEnabled = false;
    this.showIncomeModal = false;
    if (!this.activeGroup) {
      this.selectedFormGroupMembers = [];
      this.groupMembersList = [];
    }
  }

  get isEditing(): boolean {
    return this.editingTransaction !== null;
  }

  get formTitle(): string {
    return this.isEditing
      ? TEXT_CONSTANTS.TRANSACTIONS.EDIT_TRANSACTION
      : TEXT_CONSTANTS.TRANSACTIONS.NEW_TRANSACTION;
  }

  get submitButtonText(): string {
    return this.isEditing
      ? TEXT_CONSTANTS.TRANSACTIONS.UPDATE_BUTTON
      : TEXT_CONSTANTS.TRANSACTIONS.ADD_BUTTON;
  }

  get hasPromptText(): boolean {
    return !!this.promptForm.get('prompt')?.value?.trim();
  }

  get canSubmit(): boolean {
    if (this.isEditing) return this.isAmountNumeric && !!this.form.get('category_id')?.value;
    if (this.activeMode === 'prompt') return this.promptForm.valid;
    if (this.activeMode === 'manual') return this.isAmountNumeric && !!this.form.get('category_id')?.value;
    return false;
  }

  onSubmit() {
    if (this.isAmountNumeric && this.form.get('category_id')?.value) {
      if (this.form.value.date) {
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const seconds = now.getSeconds().toString().padStart(2, '0');
        this.form.value.date = `${this.form.value.date} ${hours}:${minutes}:${seconds}`;
      }

      const formValue = { ...this.form.value, tags: this.tags };

      if (!formValue.group_id) {
        formValue.group_id = null;
      }

      const selectedGroupId = this.activeGroup?.id || this.form.get('group_id')?.value;
      const selectedGroup = this.allGroupsData.find(g => g.id === selectedGroupId);
      const division = selectedGroup?.division;

      if (this.isIncome) {
        // TAR-325 (Req 3.2, 3.7, 4.8): un ingreso de grupo solo existe para
        // grupos "Todo en común" y nunca lleva split_type/splits/generates_debt.
        delete formValue.split_type;
        delete formValue.splits;
        delete formValue.generates_debt;
        if (division === 'common' && selectedGroupId) {
          formValue.group_id = selectedGroupId;
          formValue.paid_by = this.splitPaidById || undefined;
        } else {
          delete formValue.group_id;
          delete formValue.paid_by;
        }
      } else if (this.groupMembersList.length > 0 && this.splitPaidById) {
        formValue.group_id = selectedGroupId;
        formValue.paid_by = this.splitPaidById;

        if (division === 'common') {
          // TAR-325 (Req 3.1, 3.3): "Todo en común" no reparte — solo
          // registra quién lo cargó, nunca genera deuda.
          delete formValue.split_type;
          delete formValue.splits;
          formValue.generates_debt = false;
        } else if ((division === 'income' || division === 'fixed') && !this.splitOverrideEnabled) {
          // TAR-325 (Req 2, 4): sin anulación manual, el backend calcula el
          // split server-side (nunca se le manda el ingreso de nadie al cliente).
          delete formValue.split_type;
          delete formValue.splits;
          formValue.generates_debt = this.generatesDebt;
        } else {
          formValue.split_type = this.splitType;
          formValue.generates_debt = this.generatesDebt;

          let finalSplits: any[] = [];
          const totalAmount = this.form.get('amount')?.value || 0;

          if (this.splitType === 'equal') {
            finalSplits = this.groupMembersList
              .filter(m => this.splitsState[m.id].selected)
              .map(m => ({ user_id: m.id }));
          } else if (this.splitType === 'exact') {
            finalSplits = this.groupMembersList.map(m => {
              if (m.id === this.splitPaidById) {
                return { user_id: m.id, amount: this.getPaidByRemainingAmount() };
              }
              return { user_id: m.id, amount: this.splitsState[m.id].value || 0 };
            });
          } else if (this.splitType === 'percentage') {
            finalSplits = this.groupMembersList.map(m => {
              if (m.id === this.splitPaidById) {
                return { user_id: m.id, percentage: this.getPaidByRemainingPercentage() };
              }
              return { user_id: m.id, percentage: this.splitsState[m.id].value || 0 };
            });
          }

          formValue.splits = finalSplits.filter(s =>
            s.user_id === this.splitPaidById ||
            (s.amount !== 0 && s.amount !== undefined) ||
            (s.percentage !== 0 && s.percentage !== undefined) ||
            this.splitType === 'equal'
          );

          if (formValue.splits.length === 0) {
            formValue.splits = [{ user_id: this.splitPaidById, amount: totalAmount }];
          }
        }
      } else if (this.isEditing) {
        // The share add-on was removed while editing: explicitly clear every
        // group/split field so the backend unsets them (see transaction_service).
        formValue.group_id = null;
        formValue.paid_by = null;
        formValue.split_type = null;
        formValue.splits = null;
        delete formValue.generates_debt;
      } else {
        formValue.group_id = null;
      }

      this.formSubmitted.emit(formValue);
    }
  }

  onCategoryChange(selectedCategory: string | null = null) {
    this.form.patchValue({ category_id: selectedCategory });
  }

  onAddonSetAsDefault(event: { groupId: string; isDefault: boolean }): void {
    // groups$ is updated automatically inside the service via tap.
    this._groupsService.toggleDefaultGroup(event.groupId, event.isDefault).subscribe({
      next: () => {
        this.defaultGroupId = event.isDefault ? event.groupId : null;
        this._toastService.success(TEXT_CONSTANTS.COMMON.SUCCESS, TEXT_CONSTANTS.GROUPS.ACTIONS.SET_DEFAULT_SUCCESS);
      },
      error: (error) => {
        this._toastService.error(TEXT_CONSTANTS.COMMON.ERROR, this._toastService.getErrorDetail(error, TEXT_CONSTANTS.GROUPS.ACTIONS.SET_DEFAULT_ERROR));
      }
    });
  }

  onGroupChange(selectedGroup: string | null = null) {
    this.form.patchValue({ group_id: selectedGroup });
    const group = this.allGroupsData.find(g => g.id === selectedGroup);
    if (group && group.members) {
      this.selectedFormGroupMembers = group.members;
      if (!this.activeGroup) {
        this.initGroupSplits(group.members);
      }
    } else {
      this.selectedFormGroupMembers = [];
      if (!this.activeGroup) {
        this.groupMembersList = [];
      }
    }
  }

  getSelectedGroupName(): string {
    return this.allGroupsData.find(g => g.id === this.form.get('group_id')?.value)?.name || '';
  }

  closeForm() {
    this.resetForm();
    this.activeMode = null;
    this.formClosed.emit();
  }

  activatePrompt() {
    const hasContent = !!this.promptForm.get('prompt')?.value?.trim();
    this.activeMode = hasContent ? 'prompt' : null;
  }

  activateManual() {
    const amount = this.form.get('amount')?.value;
    const description = this.form.get('description')?.value?.trim();
    const categoryId = this.form.get('category_id')?.value;
    const hasContent = !!amount || !!description || !!categoryId || this.tags.length > 0;
    this.activeMode = hasContent ? 'manual' : null;
  }

  submitForm() {
    if (this.activeMode === 'prompt') {
      this.onPromptSubmit();
    } else {
      this.onSubmit();
    }
  }

  onPromptSubmit() {
    if (this.promptForm.valid) {
      const newPrompt: PromptTransaction = {
        body: this.promptForm.value.prompt,
        from_user: this._authService.getCurrentUser()!.phone_number,
      };
      if (this.activeGroup?.id) {
        newPrompt.group_id = this.activeGroup.id;
      }
      this.promptSubmitted.emit(newPrompt);
    }
  }

  canUsePrompt(): boolean {
    const user_phone = this._authService.getCurrentUser()?.phone_number;
    return !this.isEditing && user_phone !== undefined && user_phone !== null && user_phone !== '';
  }

  isMobileView(): boolean {
    return this._screenService.isMobile();
  }

  toggleCurrency(): void {
    const current = this.form.get('currency')?.value;
    const currentIndex = this.currencies.findIndex(c => c.code === current);
    const nextIndex = (currentIndex + 1) % this.currencies.length;
    this.form.patchValue({ currency: this.currencies[nextIndex].code });
    this.activateManual();
  }

  getSelectedCurrencyFlag(): string {
    const current = this.form.get('currency')?.value;
    return this.currencies.find(c => c.code === current)?.svgFlag || '';
  }

  get isIncome(): boolean {
    return this.form.get('type')?.value === 'income';
  }

  onTypeChange(type: string) {
    this.form.patchValue({ type });
    if (type !== 'income') return;

    // TAR-325 (Req 4.10): los ingresos solo se pueden compartir con grupos
    // "Todo en común" — si había un grupo no-común seleccionado, se
    // deselecciona y se avisa. Un grupo común seleccionado se mantiene.
    const selectedGroupId = this.activeGroup?.id || this.form.get('group_id')?.value;
    const selectedGroup = this.allGroupsData.find(g => g.id === selectedGroupId);
    if (selectedGroup && selectedGroup.division !== 'common') {
      this.onGroupChange(null);
      this._toastService.info(TEXT_CONSTANTS.COMMON.INFO, TEXT_CONSTANTS.TRANSACTIONS.INCOME_GROUP_DESELECTED_HINT);
    }
  }

  get isAmountNumeric(): boolean {
    const value = this.form.get('amount')?.value?.toString() || '';
    return /^\d+(\.\d+)?$/.test(value);
  }

  toggleCalculator() {
    this.showCalculator = !this.showCalculator;
  }

  insertOperator(operator: string) {
    if (operator === '=') {
      this.calculateAmount();
      return;
    }
    const currentValue = this.form.get('amount')?.value?.toString() || '';
    const newValue = `${currentValue}${operator}`;
    this.form.patchValue({ amount: newValue });
    setTimeout(() => {
      const input = this.amountInput?.nativeElement;
      if (input) {
        input.focus();
        input.setSelectionRange(newValue.length, newValue.length);
      }
    }, 0);
  }

  calculateAmount() {
    const expression = this.form.get('amount')?.value?.toString() || '';
    try {
      if (!/^[\d+\-*/.() ]+$/.test(expression)) return;
      const result = Function('"use strict"; return (' + expression + ')')();
      this.form.patchValue({ amount: Math.round(result * 100) / 100 });
      this.showCalculator = false;
    } catch (error) {
      console.error('Invalid mathematical expression', error);
    }
  }

  onAmountEnter(event: Event) {
    if (this.showCalculator) {
      event.preventDefault();
      this.calculateAmount();
    } else {
      event.preventDefault();
      this.categoryDropdown?.onInputFocus();
      const dropdownEl = this.categoryDropdown?.['elementRef']?.nativeElement?.querySelector('input');
      dropdownEl?.focus();
    }
  }

  onCategoryDropdownEnter(event: Event) {
    if (!this.categoryDropdown?.isOpen) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  getPaidByRemainingAmount(): number {
    const total = this.form.get('amount')?.value || 0;
    const sumOthers = this.groupMembersList
      .filter(m => m.id !== this.splitPaidById)
      .reduce((acc, m) => acc + (this.splitsState[m.id]?.value || 0), 0);
    return Math.max(0, total - sumOthers);
  }

  getPaidByRemainingPercentage(): number {
    const sumOthers = this.groupMembersList
      .filter(m => m.id !== this.splitPaidById)
      .reduce((acc, m) => acc + (this.splitsState[m.id]?.value || 0), 0);
    return Math.max(0, 100 - sumOthers);
  }

  onSplitValueChange(userId: string, event: Event) {
    const input = event.target as HTMLInputElement;
    let val = parseFloat(input.value) || 0;

    if (this.splitType === 'exact') {
      const total = this.form.get('amount')?.value || 0;
      const otherSum = this.groupMembersList
        .filter(m => m.id !== this.splitPaidById && m.id !== userId)
        .reduce((acc, m) => acc + (this.splitsState[m.id]?.value || 0), 0);
      if (val + otherSum > total) {
        val = Math.max(0, total - otherSum);
        input.value = val.toString();
      }
    } else if (this.splitType === 'percentage') {
      const otherSum = this.groupMembersList
        .filter(m => m.id !== this.splitPaidById && m.id !== userId)
        .reduce((acc, m) => acc + (this.splitsState[m.id]?.value || 0), 0);
      if (val + otherSum > 100) {
        val = Math.max(0, 100 - otherSum);
        input.value = val.toString();
      }
    }

    if (this.splitsState[userId]) {
      this.splitsState[userId].value = val;
    }
  }

  toggleSplitSelection(userId: string) {
    if (this.splitsState[userId]) {
      this.splitsState[userId].selected = !this.splitsState[userId].selected;
    }
  }

  onSplitTypeChange(type: string) {
    if (type === 'equal' || type === 'exact' || type === 'percentage') {
      this.splitType = type;
    }
  }
}
